import { REJECT, nullable, num, object, str, type Received } from 'validation';

import { getRecord } from 'api/shared/getRecord';

/**
 * A single lock as returned by nap-backend `GET /locks/:id` (routes/locks.js,
 * which reuses the `getLocks` list query scoped to one id). Re-derived from the
 * backend source, NOT the `locksSlice` `Lock` type, which drifted: this endpoint
 * does NOT enrich `folder_path` (only the list / POST / PUT handlers do), and it
 * DOES return `limit_allocation` (absent from the slice type). `zone_name` is
 * non-null (the zone FK is NOT NULL). Deeply readonly — validated wire data.
 *
 * This is the bare `<Entity>` — the canonical single-record read every other
 * lock shape (`LockRow`, `CreatedLock`, `UpdatedLock`) is described relative to.
 */
export interface Lock {
  readonly id: number;
  readonly name: string;
  readonly description: string | null;
  readonly folder_id: number | null;
  readonly zone_name: string;
  readonly limit: number;
  readonly limit_allocation: number;
  readonly created_at: string;
  readonly updated_at: string;
}

const lockSchema = object<Lock>({
  id: num(REJECT),
  name: str(),
  description: nullable(str()),
  folder_id: nullable(num()),
  zone_name: str(),
  limit: num(),
  limit_allocation: num(),
  created_at: str(),
  updated_at: str(),
});

/**
 * GET `/locks/:id` — one record for the edit dialog, so it works
 * when launched from the Workspace (which never populates the list slice) and
 * never opens on a loosely-cast store row.
 *
 * Boundary-validated. See `getRecord` for what each outcome means; the short
 * version is that `null` means the server said there is no such record, and
 * the failure arm means it said something we could not read. The caller must
 * tell those apart, because refreshing a list helps with only one of them.
 */
export const getLock = (id: number): Promise<Received<Lock | null>> => getRecord(`/locks/${id}`, { schema: lockSchema, source: 'getLock' });
